import { useState } from 'react';
import { Search, Info, ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import DataTable, { Column } from '@/components/DataTable';
import { PodusCheckerNavigation } from '@/components/Navbar';
import { useGetMyTransactionsQuery } from '@/store/api/apiSlice';
// import { Transaction } from '@/types';
import { useNavigate } from 'react-router';
import { useAppSelector } from '@/hooks/useAppStore';
import { selectCurrentUser } from '@/store/slices/authSlice';

// Transform API transaction data to table format
interface TransactionRow {
  id: string;
  sn: number;
  transactionId: string;
  employeeName: string | null;
  checkType: 'standard' | 'enhanced';
  datePaid: string;
  amount: number;
  status: string;
  investigationId: string | null;
  investigation?: {
    id: string;
    employeeFirstName: string;
    employeeLastName: string;
  };
}

export default function Transactions() {
  const navigate = useNavigate();
  const currentUser = useAppSelector(selectCurrentUser);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());

  // Fetch transactions from API
  const { data: transactions, isLoading, error } = useGetMyTransactionsQuery();

  // Get user initials
  const getInitials = (firstName?: string, lastName?: string) => {
    const first = firstName?.charAt(0) || '';
    const last = lastName?.charAt(0) || '';
    return (first + last).toUpperCase() || 'U';
  };

  // Transform API data to table rows
  const tableData: TransactionRow[] = transactions?.map((txn, index) => ({
    id: txn.id,
    sn: index + 1,
    transactionId: txn.transactionId,
    employeeName: txn.investigation
      ? `${txn.investigation.employeeFirstName} ${txn.investigation.employeeLastName}`
      : null,
    checkType: txn.checkType.toLowerCase() as 'standard' | 'enhanced',
    datePaid: new Date(txn.datePaid || txn.createdAt).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }),
    amount: typeof txn.amount === 'string' ? parseFloat(txn.amount) : txn.amount,
    status: String(txn.status),
    investigationId: txn.investigationId,
    ...(txn.investigation && { investigation: txn.investigation }),
  })) || [];

  const filteredData = tableData.filter((item) => {
    const matchesSearch =
      item.transactionId.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (item.employeeName?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false);
    return matchesSearch;
  });

  const handleRowSelect = (index: number) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedRows(newSelected);
  };

  const handleActivatePayment = (transactionId: string) => {
    // Navigate to new investigation page with pre-selected transaction
    navigate('/investigations/new', { state: { transactionId } });
  };

  const handleDownloadReceipt = async (transactionId: string) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'https://checker-api.podus.org/api'}/transactions/${transactionId}/receipt`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to download receipt');
      }

      // Get the blob from the response
      const blob = await response.blob();
      
      // Create a URL for the blob
      const url = window.URL.createObjectURL(blob);
      
      // Create a temporary anchor element and trigger download
      const a = document.createElement('a');
      a.href = url;
      a.download = `receipt-${transactionId}.pdf`;
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Failed to download receipt:', error);
      alert('Failed to download receipt. Please try again.');
    }
  };

  const handleViewInvestigation = (investigationId: string) => {
    navigate(`/investigations/${investigationId}`);
  };

  const columns: Column<TransactionRow>[] = [
    {
      key: 'sn',
      label: 'S/N',
      sortable: true,
      render: (item) => (
        <span className="text-sm text-gray-700">{item.sn}</span>
      ),
    },
    {
      key: 'transactionId',
      label: 'Transaction ID',
      sortable: true,
      hasMenu: true,
      render: (item) => (
        <span className="text-sm text-gray-900">{item.transactionId}</span>
      ),
    },
    {
      key: 'employeeName',
      label: 'Employee Name',
      sortable: true,
      hasMenu: true,
      render: (item) => (
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-900">
            {item.employeeName || <span className="text-gray-400 italic">Not assigned</span>}
          </span>
          {item.investigationId && (
            <button
              onClick={() => handleViewInvestigation(item.investigationId!)}
              className="text-indigo-600 hover:text-indigo-700"
              title="View Investigation"
            >
              <ExternalLink className="w-4 h-4" />
            </button>
          )}
        </div>
      ),
    },
    {
      key: 'checkType',
      label: 'Check Type',
      sortable: true,
      hasMenu: true,
      render: (item) => (
        <Badge
          variant="outline"
          className="bg-indigo-50 text-indigo-600 border-indigo-100 px-4 py-1 rounded-lg font-medium capitalize"
        >
          {item.checkType}
        </Badge>
      ),
    },
    {
      key: 'datePaid',
      label: 'Date Paid',
      sortable: true,
      hasMenu: true,
      render: (item) => (
        <span className="text-sm text-gray-700">{item.datePaid}</span>
      ),
    },
    {
      key: 'amount',
      label: 'Amount Paid',
      sortable: true,
      hasMenu: true,
      render: (item) => (
        <span className="text-sm text-gray-900 font-medium">
          ₦{item.amount.toLocaleString()}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      hasMenu: true,
      render: (item) => (
        <Badge
          variant="outline"
          className={`${item.status.toLowerCase() === 'successful'
            ? 'bg-green-50 text-green-700 border-green-100'
            : item.status.toLowerCase() === 'failed'
              ? 'bg-red-50 text-red-700 border-red-100'
              : 'bg-yellow-50 text-yellow-700 border-yellow-100'
            } px-4 py-1 rounded-lg font-medium capitalize`}
        >
          {item.status}
        </Badge>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (item) => (
        <div>
          {item.investigationId ? (
            // Payment already used - show Download Receipt
            <Button
              onClick={() => handleDownloadReceipt(item.transactionId)}
              className="bg-gray-900 hover:bg-gray-800 text-white px-6 py-2 rounded-xl text-sm font-medium"
            >
              Download Receipt
            </Button>
          ) : (
            // Payment not used yet - show Activate button
            <Button
              onClick={() => handleActivatePayment(item.transactionId)}
              className="bg-[#FF6B35] hover:bg-[#ff5722] text-white px-6 py-2 rounded-xl text-sm font-medium"
            >
              Activate
            </Button>
          )}
        </div>
      ),
    },
  ];

  // Calculate pagination
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  return (
    <main className="w-full min-h-screen lg:h-screen flex flex-col px-4 py-6 md:px-6 lg:px-8 xl:px-12 bg-[#EDEFF3]">
      <div className="bg-white rounded-3xl shadow-sm flex-1 flex flex-col overflow-hidden">
        {/* Header/Navbar */}
        <PodusCheckerNavigation
          showTitle={false}
          showInfo={false}
          showLocation={false}
          notificationCount={3}
          userName={currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'User'}
          userInitials={currentUser ? getInitials(currentUser.firstName, currentUser.lastName) : 'U'}
          only={true}
          className="border-b border-gray-100"
        />

        {/* Content */}
        <div className="flex-1 overflow-hidden p-4 md:p-8 lg:p-10 flex flex-col">
          {/* Header */}
          <div className="mb-4 md:mb-6">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3">
              Transactions
            </h1>
            <div className="flex items-start gap-2 text-gray-600">
              <Info className="w-5 h-5 text-[#FF6B35] flex-shrink-0 mt-0.5" />
              <p className="text-sm leading-relaxed">
                View all your payment transactions. Use the "Activate" button to create a new investigation with an unused payment, or download receipts for completed transactions.
              </p>
            </div>
          </div>

          {/* Search */}
          <div className="mb-4 md:mb-6">
            <div className="relative w-full sm:max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-transparent"
              />
            </div>
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="w-12 h-12 border-4 border-gray-200 border-t-gray-900 rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-600">Loading transactions...</p>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center max-w-md">
                <p className="text-red-600 mb-4">Failed to load transactions. Please try again.</p>
                <Button
                  onClick={() => window.location.reload()}
                  className="bg-gray-900 hover:bg-gray-800 text-white"
                >
                  Retry
                </Button>
              </div>
            </div>
          )}

          {/* Table Container */}
          {!isLoading && !error && (
            <>
              <div className="border border-gray-100 rounded-2xl overflow-hidden flex-1 min-h-0">
                <div className="h-full flex flex-col">
                  <DataTable
                    data={paginatedData}
                    columns={columns}
                    showCheckbox={true}
                    selectedRows={selectedRows}
                    onRowSelect={handleRowSelect}
                    hoverable={true}
                    striped={true}
                  />
                </div>
              </div>

              {/* Pagination */}
              {filteredData.length > 0 && (
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mt-6">
                  {/* Rows per page */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Rows per page:</span>
                    <select
                      value={rowsPerPage}
                      onChange={(e) => {
                        setRowsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                  </div>

                  {/* Page numbers */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      ‹
                    </button>

                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`w-8 h-8 flex items-center justify-center rounded-lg ${currentPage === pageNum
                            ? 'bg-[#FF6B35] text-white'
                            : 'text-gray-600 hover:bg-gray-50'
                            }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}

                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      ›
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </main>
  );
}
